import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from '../../../ds';
import UploadFile from '../Components/UploadFile/UploadFile';
import BulkTemplateSummary from './BulkTemplateSummary';
import ProcessingModal from '../Components/ProcessingModal/ProcessingModal';
import ErrorModal from '../Components/ErrorModal/ErrorModal';
import BulkRefundsResults from './BulkRefundsResults';
import './bulkRefundsUploadTemplate.scss';
import MerchantPaymentsService from '../../../services/MerchantPaymentsService';
import AnalyticsEvent from '../../../services/analytics/AnalyticsEvent';
import excelToJson from '../../../Utils/excelToJson';
import {
  BULK_REFUNDS_SHEETS_CONFIG,
  BULK_REFUNDS_STEP,
  ERRORS_TEMPLATE,
  REFUNDS_BULK_TEMPLATE,
} from '../../../Utils/constants';
import { useGetMerchant } from '../../../Utils/useGetMerchant';

const defaultStep = {
  step: BULK_REFUNDS_STEP.uploadFile,
  fileName: undefined,
  fileJson: undefined,
  results: {
    success_refunds_count: 0,
    errors_refunds_count: 0,
    errors_file: '',
  },
};

const BulkRefundsUploadTemplate = ({
  isOpen = false,
  onClose = () => null,
}) => {
  const userMerchant = useGetMerchant();
  const [stepFile, setStepFile] = useState(defaultStep);
  const [errorModal, setErrorModal] = useState({
    isOpen: false,
    title: '',
    message: '',
  });

  const handleClose = () => {
    setStepFile(defaultStep);
    onClose();
  };

  async function handleFilesChange(files) {
    const file = files[0];

    const validExtensions = ['.xlsx', '.xls'];
    const fileExtension = file.name
      .substring(file.name.lastIndexOf('.'))
      .toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      AnalyticsEvent().bulkRefundReportLoaded({
        action: 'error',
        error: 'ERROR_FILE_TYPE',
      });
      setErrorModal({
        isOpen: true,
        ...ERRORS_TEMPLATE.FILE_TYPE,
      });
      return;
    }

    try {
      const jsonTemplate = await excelToJson(file, BULK_REFUNDS_SHEETS_CONFIG);

      setStepFile({
        fileJson: Object.values(jsonTemplate).flat(),
        fileName: file.name ?? '',
        step: BULK_REFUNDS_STEP.bulkTemplateSummary,
      });
    } catch (error) {
      console.log('Error:', error);

      setErrorModal({
        isOpen: true,
        ...ERRORS_TEMPLATE[error.type],
      });
    }
  }

  const onProcessTemplate = async () => {
    try {
      setStepFile({
        ...stepFile,
        step: BULK_REFUNDS_STEP.bulkRefundsProcress,
      });

      const response = await MerchantPaymentsService().bulkRefunds({
        merchantId: userMerchant.merchant_id,
        jsonTemplate: stepFile.fileJson,
      });

      setStepFile({
        ...stepFile,
        step: BULK_REFUNDS_STEP.bulkRefundsResults,
        results: response,
      });

      AnalyticsEvent().bulkRefundReportLoaded({
        action: 'sucess',
      });
    } catch (error) {
      let errorType = '';
      let errorModal = {};

      if (error && error.code === 'ERR_NETWORK') {
        errorType = 'ERROR_NETWORK';
        errorModal = ERRORS_TEMPLATE.NETWORK;
      } else if (error.response && error.response.status === 403) {
        const message = error.response.data ? error.response.data.message : '';
        errorType = `BAD_REQUEST-${message}`;
        errorModal = ERRORS_TEMPLATE.NOT_FOUND_PAYMENT;
      } else if (
        error.response &&
        error.response.data &&
        error.response.data.code === 'invalid-bulk-refunds-params'
      ) {
        errorType = error.response.data.errors
          ? JSON.stringify(error.response.data.errors)
          : `BAD_REQUEST-${error.response.data.message}`;
        errorModal = ERRORS_TEMPLATE.INVALID_REFUNDS_PARAMS;
      } else {
        const message = error.response.data
          ? `BAD_REQUEST-${error.response.data.message}`
          : '';
        const errorMensage = error.message;
        errorType = `${message}${errorMensage ? `-${errorMensage}` : ''}`;
        errorModal = ERRORS_TEMPLATE.BAD_REQUEST;
      }

      setStepFile(defaultStep);
      setErrorModal({
        isOpen: true,
        ...errorModal,
      });
      AnalyticsEvent().bulkRefundReportLoaded({
        action: 'error',
        error: errorType,
      });
    }
  };

  const downloadFile = ({ url }) => {
    window.open(url, '_blank');
  };

  return (
    <>
      <Modal
        className='bulk-refunds-upload-template'
        theme='dark'
        cardType='secondary'
        onClose={handleClose}
        onOverlayClick={handleClose}
        isOpen={isOpen}
        showClose={stepFile.step != BULK_REFUNDS_STEP.bulkRefundsProcress}
      >
        {stepFile.step === BULK_REFUNDS_STEP.uploadFile && (
          <UploadFile
            onChange={handleFilesChange}
            onDownload={() => {
              downloadFile({
                url: REFUNDS_BULK_TEMPLATE,
                fileName: REFUNDS_BULK_TEMPLATE,
              });
            }}
          />
        )}
        {stepFile.step === BULK_REFUNDS_STEP.bulkTemplateSummary && (
          <BulkTemplateSummary
            fileName={stepFile.fileName}
            onContinue={onProcessTemplate}
          />
        )}
        {stepFile.step === BULK_REFUNDS_STEP.bulkRefundsProcress && (
          <ProcessingModal
            title='Estamos procesando las cancelaciones'
            message='No actualices o cierres esta ventana, puede tomar unos momentos.'
          />
        )}
        {stepFile.step === BULK_REFUNDS_STEP.bulkRefundsResults && (
          <BulkRefundsResults
            successRefunds={stepFile.results.success_refunds_count}
            errorsRefunds={stepFile.results.errors_refunds_count}
            onDownloadResults={() => {
              downloadFile({
                url: stepFile.results.errors_file,
                fileName: 'bulk-refunds-errors.xlsx',
              });
            }}
          />
        )}
      </Modal>
      <ErrorModal
        isOpen={errorModal.isOpen}
        title={errorModal.title}
        message={errorModal.message}
        onClose={() => {
          setErrorModal(false);
        }}
        textButton='Cerrar'
      />
    </>
  );
};

BulkRefundsUploadTemplate.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default BulkRefundsUploadTemplate;
